import { createApi } from '@reduxjs/toolkit/query/react';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';
import { usersType } from '@store/slices/users/usersSlice';
import { baseQuerySample } from '@store/helpers';
import { getPermissionLabels } from '@utils/inputTitles';

export type MfaDeliveryMethod = 'EMAIL' | 'TEXT_MESSAGE';

export type UserApiScope = 'payor' | 'contractor';

export type MfaLoginChallengeMethod = {
  method: MfaDeliveryMethod;
  enabled: boolean;
  maskedContact: string | null;
  disabledReason: string | null;
};

export type MfaLoginResponse = {
  authenticated: boolean;
  mfaRequired: boolean;
  sidCookie: string | null;
  mfaChallenge: {
    mfaRequired: boolean;
    challengeToken: string;
    methods: MfaLoginChallengeMethod[];
  } | null;
};

export type MfaChallenge = {
  challengeToken: string;
  method: MfaDeliveryMethod;
  maskedContact: string;
  expiresAt: string;
  remainingCodeRequests: number;
};

export const authLoginApi = createApi({
  reducerPath: 'Auth',
  tagTypes: ['Auth'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    authLogin: builder.query({
      query: () => {
        return {
          url: `/v1/users/info/profile`,
        };
      },
    }),

    sendMfaCode: builder.mutation<
      MfaChallenge,
      { challengeToken: string; method: MfaDeliveryMethod }
    >({
      query: (body) => ({
        url: `/auth/mfa/send-code`,
        method: 'POST',
        body,
      }),
    }),

    resendMfaCode: builder.mutation<MfaChallenge, { challengeToken: string }>({
      query: (body) => ({
        url: `/auth/mfa/resend`,
        method: 'POST',
        body,
      }),
    }),

    verifyMfaCode: builder.mutation<
      unknown,
      { challengeToken: string; code: string }
    >({
      query: (body) => ({
        url: `/auth/mfa/verify`,
        method: 'POST',
        body,
      }),
    }),

    getContractorVersion: builder.query<boolean, { payerId: number }>({
      query: ({ payerId }) => ({
        url: `/v1/company/info/contractor-version`,
        params: { payerId },
      }),
    }),

    getUsers: builder.query<
      {
        content: Array<usersType>;
        last?: boolean;
        page?: number;
        size?: number;
        totalElements?: number;
        totalPages?: number;
      },
      {
        scope?: UserApiScope;
        argument?: { scope?: UserApiScope };
        body?: Record<string, string>;
        params?: {
          page?: number;
          size?: number;
          sortBy?: string;
          sortDir?: string;
        };
      }
    >({
      query: ({
        scope: requestedScope = 'payor',
        argument,
        params = {},
        body = {},
      }) => {
        const scope = argument?.scope ?? requestedScope;
        if (scope === 'contractor') {
          return {
            url: '/v2/users/contractor',
            params: {
              pageNo: params.page ?? 0,
              pageSize: params.size ?? 10,
            },
          };
        }

        return {
          url: '/v1/users/payors',
          method: 'POST',
          body,
          params: {
            sortBy: 'firstName',
            sortDir: 'desc',
            page: 0,
            size: 8,
            ...params,
          },
        };
      },
    }),

    getUserByRoleId: builder.query({
      query: ({ id }) => ({
        url: `/v1/users/info/list/role/${id}`,
      }),
    }),

    createOrUpdateUser: builder.mutation({
      query: ({ id, body, scope = 'payor' }) => {
        if (scope === 'contractor') {
          return {
            url: `/v2/users/contractor/${id ? `update/${id}` : 'create'}`,
            method: 'POST',
            body,
          };
        }

        return {
          url: `/v1/users/payors/${id ? `update/${id}` : 'create'}`,
          method: id ? 'PUT' : 'POST',
          body,
        };
      },
    }),

    getUserPermissionsTypeSelectTable: builder.query({
      query: ({ total, params = {} }) => {
        return {
          url: `/v1/users/info/type/all`,
          params: {
            sortBy: 'actionTime',
            sortDir: 'desc',
            page: 0,
            size: total,
            ...params,
          },
        };
      },
    }),

    getUserPermissionsTypeSelect: builder.query({
      query: ({ id }) => ({
        // TODO check this in the future!! changed by Areg's request!!
        // url: `/v1/users/info/role-types/all`,
        url: `/v1/users/info/select/role/${id}`,
      }),
    }),

    getUserPermissionsSelect: builder.query({
      query: ({ id }) => ({
        url: `/v1/users/info/role-types/${id}`,
      }),
    }),

    getPermissionsById: builder.query({
      query: ({ argument: { id, size }, params = {}, body = {} }) => ({
        url: `/v1/users/info/permissions/${id}`,
        method: 'POST',
        body,
        params: {
          page: 0,
          // size: BIG_TABLE_DEFAULT_PAGE_SIZE,
          size,
          ...params,
        },
      }),
    }),

    // the logged-in user's own permissions (same source as Home), filtered and paged locally
    getProfilePermissions: builder.query<any, any>({
      queryFn: ({ params = {}, body = {} } = {}, { getState }) => {
        const { newPermissions } = (
          getState() as { users: { newPermissions: any } }
        ).users;

        const permissions = (newPermissions ?? [])
          .flatMap((module) => module.permissions ?? [])
          .map((permission) => ({
            permission,
            labels: getPermissionLabels(permission),
          }))
          .filter(({ labels }) =>
            Object.entries(body ?? {}).every(([key, filter]: [string, any]) => {
              const label = (
                { module: labels.module, name: labels.tab }[key] ?? ''
              ).toLowerCase();
              const value = String(filter?.value ?? '')
                .trim()
                .toLowerCase();

              return filter?.operator === 'equals'
                ? label === value
                : label.includes(value);
            })
          )
          .sort(
            (a, b) =>
              a.labels.module.localeCompare(b.labels.module) ||
              a.labels.tab.localeCompare(b.labels.tab)
          );

        const size = params.size ?? 10;
        const totalPages = Math.ceil(permissions.length / size);
        const page = Math.min(params.page ?? 0, Math.max(totalPages - 1, 0));

        return {
          data: {
            content: permissions
              .slice(page * size, page * size + size)
              .map(({ permission }) => permission),
            page,
            size,
            totalElements: permissions.length,
            totalPages,
            filter: body ?? {},
          },
        };
      },
      keepUnusedDataFor: 0,
    }),

    setNewPassword: builder.mutation({
      query: (body) => ({
        url: `/auth/password/set-password`,
        method: 'POST',
        body,
      }),
    }),

    getPermissionsByRoleId: builder.query({
      query: ({ id }) => ({
        url: `/v1/users/info/roles/permissions/${id}`,
      }),
    }),

    getUserActivity: builder.query({
      query: ({ argument: { id }, params = {} }) => ({
        url: `/v1/logs/audit/modification/${id}`,
        params: {
          sortBy: 'actionTime',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),

    getUserViews: builder.query({
      query: ({ argument: { id }, params = {} }) => ({
        url: `/v1/logs/audit/view/${id}`,
        params: {
          sortBy: 'timestamp',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getUserLogins: builder.query({
      query: ({ argument: { email }, params = {} }) => ({
        url: `/v1/logs/audit/login-log/${email}`,
        params: {
          sortBy: 'timestamp',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),

    getUserById: builder.query({
      query: ({ id }) => ({
        url: `/v1/users/info/user/${id}`,
      }),
    }),

    getAbilities: builder.query({
      query: () => ({
        url: `/v1/users/abilities`,
      }),
    }),

    forgotPassword: builder.mutation({
      query: (body) => ({
        url: `/auth/password/reset`,
        method: 'POST',
        body,
        responseHandler: (response) => response.text(),
      }),
    }),

    changePassword: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/users/payors/change-password`,
        method: 'POST',
        body,
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ id, body }) => ({
        url: `/v1/users/payors/reset-password/${id}`,
        method: 'POST',
        body,
      }),
    }),

    resetPasswordWithEmail: builder.mutation({
      query: ({ body }) => ({
        url: `/auth/password/reset-password`,
        method: 'POST',
        body,
      }),
    }),

    sessionValidate: builder.query({
      query: () => ({
        url: `/auth/session/validate`,
        method: 'GET',
      }),
    }),

    refreshToken: builder.mutation({
      query: (body) => ({
        url: `/auth/refresh-token`,
        method: 'POST',
        body,
      }),
    }),

    logOut: builder.mutation({
      query: (body) => ({
        url: `/auth/logout`,
        method: 'POST',
        body,
      }),
    }),

    unlockUser: builder.mutation({
      query: ({ body = {}, id }) => ({
        url: `/v1/users/payors/unlock/${id}`,
        method: 'PUT',
        body,
      }),
    }),

    addTag: builder.mutation({
      query: (body) => ({
        url: '/v1/users/user-tags',
        method: 'POST',
        body: body,
      }),
    }),
    deleteTag: builder.mutation({
      query: (id) => ({
        url: `/v1/users/user-tags/${id}`,
        method: 'DELETE',
      }),
    }),
    updateUserRoles: builder.mutation({
      query: (data) => ({
        url: `/v1/users/roles/updateRole`,
        method: 'POST',
        body: data,
      }),
    }),

    changeAbilities: builder.mutation<
      {
        success: boolean;
        massage: string;
      },
      {
        permissionId: number;
        abilityId: number;
      }
    >({
      query: (body) => ({
        url: `/v1/users/abilities/updateRole`,
        method: 'POST',
        body,
      }),
    }),
    setMessage: builder.mutation({
      query: (body: {
        phoneNumber: string;
        rideId: number;
        rideRequestDate: string;
        url: string;
      }) => {
        return {
          url: `/rides/sms/set`,
          method: 'POST',
          body,
        };
      },
    }),
    rideshareAction: builder.mutation({
      query: ({
        body,
        params,
      }: {
        body: {
          segmentId: number;
          estDropMiles?: number;
          estimatedDropTripDuration?: string;
          scheduledAppointmentTime?: string;
          scheduledPickupTime?: string;
          reason?: string;
          canceledBy?: string;
          action: 'cancel' | 'activate';
        };
        params: any;
      }) => {
        return {
          url: `/rides/sms/activate`,
          method: 'POST',
          body,
          params,
          responseHandler: 'content-type',
        };
      },
    }),
    setStatus: builder.mutation({
      query: ({ status }: { status: 'Away' | 'Online' }) => {
        return {
          url: `/auth/status/status`,
          method: 'POST',
          params: { status },
        };
      },
    }),
    getMessageInfo: builder.query({
      query: (params) => {
        return {
          url: '/rides/sms/get',
          method: 'GET',
          params,
        };
      },
    }),
    getTokenStatus: builder.query<
      { status: 'VALID' | 'ALREADY_USED' | 'EXPIRED' | 'NOT_FOUND' },
      { token: string }
    >({
      query: ({ token }) => {
        return {
          url: '/auth/password/reset/validate',
          method: 'GET',
          params: { token },
        };
      },
    }),
  }),
});

export const {
  useUpdateUserRolesMutation,
  useLazyAuthLoginQuery,
  useLazyGetContractorVersionQuery,
  useResetPasswordWithEmailMutation,
  useAuthLoginQuery,
  useChangePasswordMutation,
  useResetPasswordMutation,
  useUnlockUserMutation,
  useForgotPasswordMutation,
  useLazyGetUsersQuery,
  // useGetUsersMutation,
  useLazyGetUserByRoleIdQuery,
  useCreateOrUpdateUserMutation,
  useLazyGetUserPermissionsTypeSelectTableQuery,
  useLazyGetUserPermissionsTypeSelectQuery,
  useLazyGetUserPermissionsSelectQuery,
  useLazyGetPermissionsByIdQuery,
  useLazyGetProfilePermissionsQuery,
  useLazyGetUserActivityQuery,
  useLazyGetUserViewsQuery,
  useLazyGetUserLoginsQuery,
  useLazyGetUserByIdQuery,
  useChangeAbilitiesMutation,
  useLazyGetAbilitiesQuery,
  useAddTagMutation,
  useDeleteTagMutation,
  useLazyGetPermissionsByRoleIdQuery,
  useSetNewPasswordMutation,
  useLazySessionValidateQuery,
  useRefreshTokenMutation,
  useLogOutMutation,
  useSetMessageMutation,
  useRideshareActionMutation,
  useLazyGetMessageInfoQuery,
  useSetStatusMutation,
  useLazyGetTokenStatusQuery,
  useSendMfaCodeMutation,
  useResendMfaCodeMutation,
  useVerifyMfaCodeMutation,
} = authLoginApi;
